import React, { useContext, useEffect } from 'react';
import logo from 'assets/images/portal-logo.svg';
import { toaster } from 'components/v2/toast';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Link, RouteComponentProps, useHistory } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { LoginModel, UserModel } from 'services/EpdClient';
import styled from 'styled-components';
import { HeaderLogo, HyperLink } from 'styles/Styles.styled';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import LoginBanner from './LoginBanner';
import InputFormField from 'components/v2/form/InputFormField';
import { PrimaryButton } from 'components/v2/buttons';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from 'services/api/constants';
import AdminService from 'services/admin/AdminService';
import AuthTitle from '../../../components/auth/AuthTitle';
import AuthLayoutFrameFooterResources from 'components/auth/AuthLayoutFrameFooterResources';

const ERROR_MARKER_JUST_SO_WE_PAINT_INPUT_BORDER_RED_FOR_A_WHILE = '!';

const Login: React.FC<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const { changeCompany, setLatestOrFirstOrDefaultCompany } = useContext(CompanyContext);
  const history = useHistory();
  const publicMessage = useQuery({
    queryKey: [QUERY_KEYS.PUBLIC_MESSAGE],
    queryFn: () => AdminService.getPublicInformationMessage(),
  }).data;

  const initialValues: LoginModel = {
    userName: process.env.REACT_APP_DEBUG && process.env.REACT_APP_LOGIN ? process.env.REACT_APP_LOGIN : '',
    password: process.env.REACT_APP_DEBUG && process.env.REACT_APP_PASSWORD ? process.env.REACT_APP_PASSWORD : '',
  };

  const validationSchema = Yup.object({
    userName: Yup.string().required('Required'),
    password: Yup.string().required('Required'),
  });

  const redirectToDashboard = (result: UserModel) => {
    let nextUrl;
    const storedRedirect = window.sessionStorage.getItem('redirectUrl');
    if (storedRedirect) {
      window.sessionStorage.removeItem('redirectUrl');
      nextUrl = storedRedirect;
    } else {
      nextUrl = EpdLinks.dashboard();
    }

    if (result.latestLoginCompanyId) {
      changeCompany(result.latestLoginCompanyId);
    } else if (result.memberships.length > 0) {
      changeCompany(result.memberships[0].companyId);
    } else {
      setLatestOrFirstOrDefaultCompany();
    }

    history.push(nextUrl);
  };

  const login = async (values: LoginModel, formikHelpers: FormikHelpers<LoginModel>) => {
    try {
      const result = await AuthService.login(values);
      redirectToDashboard(result);
    } catch (e) {
      notifyAboutLoginError(e);
      formikHelpers.setErrors({
        userName: ERROR_MARKER_JUST_SO_WE_PAINT_INPUT_BORDER_RED_FOR_A_WHILE,
        password: ERROR_MARKER_JUST_SO_WE_PAINT_INPUT_BORDER_RED_FOR_A_WHILE,
      });
    }
  };

  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      redirectToDashboard(AuthService.getUser()!);
    }
  }, []);

  return (
    <LoginContainer>
      <MainColumn>
        <HeaderLogo src={logo} alt="EPD International" />
        <BodyContainer>
          {publicMessage && <PublicMessage>{publicMessage}</PublicMessage>}
          <AuthTitle>Log in</AuthTitle>
          <RegisterText>
            New to the Portal?{' '}
            <HyperLink as={Link} to={EpdRoutes.account.register.user}>
              Register account
            </HyperLink>
          </RegisterText>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={login}>
            {({ isSubmitting, handleSubmit, errors }) => (
              <FormWrapper onSubmit={handleSubmit}>
                <InputFormField
                  label="Email"
                  name="userName"
                  type="userName"
                  placeholder="email@example.com"
                  autoComplete="username"
                  errorPositioning={
                    errors.userName === ERROR_MARKER_JUST_SO_WE_PAINT_INPUT_BORDER_RED_FOR_A_WHILE ? 'none' : 'absolute'
                  }
                />
                <InputFormField
                  label="Password"
                  name="password"
                  type="password"
                  placeholder="Type password here"
                  autoComplete="current-password"
                  style={{ marginTop: 30 }}
                  errorPositioning={
                    errors.userName === ERROR_MARKER_JUST_SO_WE_PAINT_INPUT_BORDER_RED_FOR_A_WHILE ? 'none' : 'static'
                  }
                />
                <ForgotPasswordWrapper>
                  <HyperLink as={Link} to={EpdRoutes.account.forgotPassword}>
                    Forgot password?
                  </HyperLink>
                </ForgotPasswordWrapper>
                <LoginButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Logging in...' : 'Log in'}
                </LoginButton>
              </FormWrapper>
            )}
          </Formik>
        </BodyContainer>
        <Footer>
          {t('footer.text')}
          {' | '}
          <a href={t('footer.link')} target="_blank">
            {t('footer.linkText')}
          </a>
          {' | '}
          <AuthLayoutFrameFooterResources />
        </Footer>
      </MainColumn>
      <BlurbColumn>
        <LoginBanner />
      </BlurbColumn>
    </LoginContainer>
  );
};

function notifyAboutLoginError(error: any) {
  const errorCode = PoorMansError(error);
  switch (errorCode) {
    case 'InvalidCredentials':
      toaster({
        severity: 'error',
        summary: 'Email or password is incorrect.',
        details: 'Please check your data and try to log in again.',
      });
      break;
    case 'ConfirmEmail':
      toaster({
        severity: 'error',
        summary: 'You need to confirm your email address!',
        details: (
          <>
            Please follow the link sent to your mailbox
            <br />
            to complete verification on the EPD Portal.
          </>
        ),
      });
      break;
    case 'LockedOut':
      toaster({
        severity: 'error',
        summary: 'User account is locked out.',
        details: 'Maximum log in attempts reached, please try again in a few hours or contact support.',
      });
      break;
    default:
      toaster({ severity: 'error', summary: 'Unexpected error', details: errorCode });
  }
}

const LoginContainer = styled.div`
  font-family: Inter;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;

  ${(props) => props.theme.media.desktop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const MainColumn = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr max-content;
  align-items: center;
`;

const BodyContainer = styled.div`
  margin-left: 16px;
  margin-right: 16px;
  margin-top: 40px;

  ${(props) => props.theme.media.tablet} {
    margin-left: 80px;
    margin-right: 80px;
  }

  ${(props) => props.theme.media.desktop} {
    max-width: 440px;
  }
`;

const PublicMessage = styled.div`
  position: absolute;
  z-index: 1;
  top: 16px;
  left: 50%;
  width: 50vw;
  transform: translateX(-50%);
  color: ${(props) => props.theme.colors.statusLabelRedText};
  margin-bottom: 16px;
  background-color: #ffffff7f;
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.04);
`;

const RegisterText = styled.div`
  margin-top: 30px;
  font-size: 16px;
`;

const FormWrapper = styled.form`
  margin-top: 60px;
  display: grid;
`;

const ForgotPasswordWrapper = styled.div`
  margin-top: 8px;
  font-size: 14px;
`;

const LoginButton = styled(PrimaryButton)`
  margin-top: 60px;
  justify-content: center;
`;

const Footer = styled.div`
  padding-top: 32px;
  padding-bottom: 40px;
  padding-left: 40px;
  &,
  a {
    font-size: 14px;
    color: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const BlurbColumn = styled.div`
  display: none;

  ${(props) => props.theme.media.desktop} {
    display: block;
  }
`;

export default Login;
